import { ChevronLeft, ChevronRight } from "lucide-react";
import React from "react";
import PageSizeSelector from "../PageSizeSelector";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  showPageSizeSelector?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  hasNextPage,
  hasPreviousPage,
  showPageSizeSelector = true,
}) => {
  const generatePageNumbers = () => {
    const pages = [];
    const showPages = 7;

    if (totalPages <= showPages) {
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 0; i < 5; i++) {
          pages.push(i);
        }
        pages.push(-1); // ellipsis
        pages.push(totalPages - 1);
      } else if (currentPage >= totalPages - 4) {
        pages.push(0);
        pages.push(-1); // ellipsis
        for (let i = totalPages - 5; i < totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(0);
        pages.push(-1); // ellipsis
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push(-1); // ellipsis
        pages.push(totalPages - 1);
      }
    }

    return pages;
  };

  return (
    <div className="flex w-full items-center justify-center md:justify-end">
      <div className="flex w-full flex-col items-center gap-3 rounded-[10px] bg-white px-2 py-3 sm:flex-row sm:justify-between sm:px-6 md:w-auto md:min-h-[52px] md:gap-8">
        {/* Left side - Navigation */}
        <div className="flex max-w-full items-center space-x-2">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={!hasPreviousPage}
            className={`
            flex items-center justify-center w-8 h-8 transition-colors border border-[#EFEFEF] rounded-lg
            ${
              !hasPreviousPage
                ? "text-gray-400 cursor-not-allowed"
                : "text-hColor hover:bg-white hover:text-gray-800"
            }
          `}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          <div className="flex items-center space-x-0.5 sm:space-x-1">
            {generatePageNumbers().map((page, index) => (
              <React.Fragment key={index}>
                {page === -1 ? (
                  <span className="flex items-center justify-center w-8 h-8 text-sm text-gray-500">
                    ...
                  </span>
                ) : (
                  <button
                    onClick={() => onPageChange(page + 1)}
                    className={`
          flex items-center justify-center w-8 h-8 text-sm font-medium rounded-md transition-colors
          ${
            page + 1 === currentPage
              ? "bg-[#F1F1F1] text-hColor"
              : "text-hColor hover:bg-white hover:text-gray-800"
          }
        `}
                  >
                    {page + 1}
                  </button>
                )}
              </React.Fragment>
            ))}
          </div>

          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={!hasNextPage}
            className={`
            flex items-center justify-center w-8 h-8 transition-colors border border-[#EFEFEF] rounded-lg
            ${
              !hasNextPage
                ? "text-gray-400 cursor-not-allowed"
                : "text-hColor hover:bg-white hover:text-gray-800"
            }
          `}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* Right side - Rows per page */}
        {showPageSizeSelector && (
          <PageSizeSelector
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
          />
        )}
      </div>
    </div>
  );
};

export default Pagination;
